import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './BottomNavigation.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNotificationsContext } from './NotificationsContext';
import QRScanner from './QRScanner';
// import VoiceRecorder from './VoiceRecorder';  // Temporär deaktiviert
import Toast from './Toast';
import { getContentByHash, getElementByHash, getCollectionByHash } from '../services/apiService';
import { appConfig } from '../config';

const BottomNavigation = ({ onVoiceSearchResults, onRagAnswer }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, makeAuthenticatedRequest, user } = useAuth();
  const { t } = useLanguage();
  const { notifications } = useNotificationsContext();
  const unreadCount = notifications.length;
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  // const [isVoiceRecorderOpen, setIsVoiceRecorderOpen] = useState(false);  // Temporär deaktiviert
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [unknownQRModal, setUnknownQRModal] = useState({ isOpen: false, scannedCode: null });

  // ── Long-Press auf Kamera-Button → ElementCreator ─────────────────────────
  // Kurzes Tippen (<2s) → QR-Scanner (bestehend)
  // Langes Halten (≥2s) → ElementCreator (nur Gruppen 0/2/3)
  const longPressTimer      = useRef(null);
  const longPressTriggered  = useRef(false);
  const [cameraLongPress, setCameraLongPress] = useState(false); // visuelles Feedback

  const handleCameraPointerDown = (e) => {
    longPressTriggered.current = false;
    const allowedGroups = [0, 2, 3];
    if (!user || !allowedGroups.includes(Number(user.group))) return;

    setCameraLongPress(true);
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      setCameraLongPress(false);
      if (navigator.vibrate) navigator.vibrate(50);
      navigate('/create-element');
    }, 1600);
  };

  const handleCameraPointerUp = () => {
    clearTimeout(longPressTimer.current);
    setCameraLongPress(false);
  };

  const handleCameraClick = () => {
    if (longPressTriggered.current) {
      longPressTriggered.current = false;
      return; // Long-Press hat bereits navigiert → kein QR-Scanner
    }
    setIsQRScannerOpen(true);
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Close QR scanner when navigating away
  useEffect(() => {
    if (isQRScannerOpen) {
      setIsQRScannerOpen(false);
    }
  }, [location.pathname]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setIsQRScannerOpen(false);
      // setIsVoiceRecorderOpen(false);  // Temporär deaktiviert
    };
  }, []);

  const getActiveNavItem = () => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return 'home'; 
    if (path === '/my-courses') return 'myCourses';
    if (path === '/challenges') return 'challenges';
    if (path === '/profile') return 'profile';
    if (path.startsWith('/course/')) return 'myCourses';
    if (path.startsWith('/element/')) return 'home';
    return '';
  };

  const activeNavItem = getActiveNavItem();

  // URL opening function - optimized for iOS in-app WebView
  const openUrlInSameTab = (url) => {
    try {
      console.log('Opening URL:', url);
      console.log('User Agent:', navigator.userAgent);
      console.log('Platform:', navigator.platform);
      
      const currentUrl = window.location.href;
      window.location.href = url;
      
      setTimeout(() => {
        if (window.location.href === currentUrl) {
          console.warn('URL navigation may have failed - still on same page');
          setToast({ 
            show: true, 
            message: `Failed to navigate to: ${url}. URL may be blocked or invalid.`, 
            type: 'error'
          });
          
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(() => {
              setTimeout(() => {
                setToast({ 
                  show: true, 
                  message: 'URL copied to clipboard as fallback', 
                  type: 'info'
                });
              }, 2000);
            }).catch((clipboardError) => {
              console.warn('Clipboard copy also failed:', clipboardError);
            });
          }
        }
      }, 2000);
      
    } catch (error) {
      console.warn('Error opening URL:', error);
      const errorMessage = error.message || error.toString() || 'Unknown error occurred';
      setToast({ 
        show: true, 
        message: `Failed to open URL: ${errorMessage}`, 
        type: 'error'
      });
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          setTimeout(() => {
            setToast({ 
              show: true, 
              message: 'URL copied to clipboard as fallback', 
              type: 'info'
            });
          }, 2000);
        }).catch((clipboardError) => {
          console.warn('Clipboard copy also failed:', clipboardError);
        });
      }
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  // Temporär deaktiviert — für spätere Reaktivierung erhalten
  // const handleVoiceSearchResults = (results) => {
  //   setToast({ 
  //     show: true, 
  //     message: (t('voiceSearchSummary') || '{collections} collections, {elements} elements')
  //       .replace('{collections}', String(results.data?.collections?.length || 0))
  //       .replace('{elements}', String(results.data?.elements?.length || 0)), 
  //     type: 'success' 
  //   });
  //   if (onVoiceSearchResults) {
  //     onVoiceSearchResults(results);
  //   } else {
  //     navigate('/dashboard', { 
  //       state: { voiceSearchResults: results, fromVoiceSearch: true } 
  //     });
  //   }
  // };

  const handleQRScan = async (data) => {
    if (!user) {
      setToast({ 
        show: true, 
        message: t('pleaseLoginFirst') || 'Please login first to scan QR codes', 
        type: 'error' 
      });
      return;
    }
    
    try {
      const trimmed = (data || '').trim();
      const isEAN = (text) => /^\d{8}$|^\d{12}$|^\d{13}$|^\d{14}$/.test((text || '').trim());
      const isAlnumHash = /^[A-Za-z0-9\-_]+$/.test(trimmed);
      
      const isWebAppUrl = (url) => {
        const baseUrl = appConfig.webAppBaseUrl;
        try {
          const urlObj = new URL(url);
          const hostname = urlObj.hostname.toLowerCase();
          if (hostname === baseUrl.toLowerCase()) return true;
          if (hostname.endsWith('.' + baseUrl.toLowerCase())) return true;
          const parts = hostname.split('.');
          for (let i = 0; i < parts.length - 1; i++) {
            if (parts.slice(i).join('.') === baseUrl.toLowerCase()) return true;
          }
          return false;
        } catch (e) {
          return url.toLowerCase().includes(baseUrl.toLowerCase());
        }
      };
      
      const isHejviUrl = /(^https?:\/\/[^\s]*\.hejvi\.[^\s/]+(\/[^\s]*)?$)|(^https?:\/\/hejvi\.[^\s/]+(\/[^\s]*)?$)/i.test(trimmed);
      const looksLikeUrl = /^https?:\/\//i.test(trimmed);

      if (looksLikeUrl && !isHejviUrl && !isWebAppUrl(trimmed)) {
        setUnknownQRModal({ isOpen: true, scannedCode: trimmed });
        return;
      }

      if (isHejviUrl || isWebAppUrl(trimmed)) {
        setToast({ show: true, message: t('openingUrl') || 'Opening URL...', type: 'info' });
        try {
          new URL(trimmed);
        } catch (urlError) {
          setToast({ show: true, message: `Invalid URL format: ${trimmed}`, type: 'error' });
          return;
        }
        openUrlInSameTab(trimmed);
        setTimeout(() => {
          setToast({ show: true, message: t('urlOpened') || 'URL opened successfully', type: 'success' });
        }, 1500);
      } else if (data.startsWith('course:')) {
        const courseId = data.replace('course:', '');
        navigate(`/course/${courseId}`);
        setToast({ show: true, message: t('navigatingCourse'), type: 'success' });
      } else if (data.startsWith('product:')) {
        const productCode = data.replace('product:', '');
        setToast({ show: true, message: t('productCode').replace('{code}', productCode), type: 'info' });
      } else if (isEAN(trimmed)) {
        const ean = trimmed;
        setToast({ show: true, message: t('lookingUpEAN').replace('{ean}', ean), type: 'info' });

        const response = await makeAuthenticatedRequest('/feed');
        const feedJson = await response.json();
        const collections = Array.isArray(feedJson?.data) ? feedJson.data : (Array.isArray(feedJson) ? feedJson : []);
        const getItemEAN = (item) => String(item?.EAN || item?.ean || item?.ean_number || '').trim();

        let matchElement = null;
        let matchCollection = null;

        for (const col of collections) {
          if (!matchCollection && getItemEAN(col) === ean) matchCollection = col;
          const elements = Array.isArray(col?.elements) ? col.elements : [];
          for (const el of elements) {
            if (getItemEAN(el) === ean) { matchElement = el; break; }
          }
          if (matchElement) break;
        }

        if (matchElement?.hash_id) {
          const full = await getElementByHash(matchElement.hash_id);
          navigate(`/feed/${matchElement.hash_id}`, {
            state: { elementData: full?.data, chapters: full?.chapters || [], related: full?.related || [], sourcePage: location.pathname || '/dashboard' }
          });
          setToast({ show: true, message: t('elementFound').replace('{name}', matchElement.name || matchElement.hash_id), type: 'success' });
          return;
        }

        if (matchCollection?.hash_id) {
          const full = await getCollectionByHash(matchCollection.hash_id);
          navigate(`/collection/${matchCollection.hash_id}`, {
            state: { collectionData: full?.data, chapters: full?.chapters || [], related: full?.related || [], sourcePage: location.pathname || '/dashboard' }
          });
          setToast({ show: true, message: t('collectionFound').replace('{name}', matchCollection.name || matchCollection.hash_id), type: 'success' });
          return;
        }

        setUnknownQRModal({ isOpen: true, scannedCode: trimmed });
        return;
      } else if (trimmed.startsWith('el-')) {
        setToast({ show: true, message: t('fetchingContent'), type: 'info' });
        const full = await getElementByHash(trimmed);
        if (appConfig?.workflow?.directVideoPlay) {
          navigate(`/element-feed/${trimmed}`, {
            state: { elementData: full?.data, chapters: full?.chapters || [], related: full?.related || [], fromQRScan: true, sourcePage: location.pathname || '/dashboard', lessons: full?.related || [], startIndex: 0 }
          });
        } else {
          navigate(`/element/${trimmed}`, {
            state: { elementData: full?.data, chapters: full?.chapters || [], related: full?.related || [], fromQRScan: true, sourcePage: location.pathname || '/dashboard' }
          });
        }
        setToast({ show: true, message: t('elementFound').replace('{name}', full?.data?.name || trimmed), type: 'success' });
        return;
      } else if (trimmed.startsWith('col-')) {
        setToast({ show: true, message: t('fetchingContent'), type: 'info' });
        const full = await getCollectionByHash(trimmed);
        navigate(`/collection/${trimmed}`, {
          state: { collectionData: full?.data, chapters: full?.chapters || [], related: full?.related || [], sourcePage: location.pathname || '/dashboard' }
        });
        setToast({ show: true, message: t('collectionFound').replace('{name}', full?.data?.name || trimmed), type: 'success' });
        return;
      } else if (isAlnumHash) {
        setToast({ show: true, message: t('fetchingContent'), type: 'info' });

        try {
          const [colResult, elResult] = await Promise.allSettled([
            getCollectionByHash(`col-${trimmed}`),
            getElementByHash(`el-${trimmed}`),
          ]);

          const colValue = colResult.status === 'fulfilled' ? colResult.value : null;
          const colData  = colValue?.data ?? (colValue?.hash_id ? colValue : null);

          if (colData) {
            const colHash = `col-${trimmed}`;
            navigate(`/collection/${colHash}`, {
              state: { collectionData: colData, chapters: colValue?.chapters || [], related: colValue?.related || [], sourcePage: location.pathname || '/dashboard' }
            });
            setToast({ show: true, message: t('collectionFound').replace('{name}', colData?.name || colHash), type: 'success' });
            return;
          }

          const elValue = elResult.status === 'fulfilled' ? elResult.value : null;
          const elData  = elValue?.data ?? (elValue?.hash_id ? elValue : null);

          if (elData) {
            const elHash = `el-${trimmed}`;
            navigate(`/element/${elHash}`, {
              state: { elementData: elData, chapters: elValue?.chapters || [], related: elValue?.related || [], fromQRScan: true, sourcePage: location.pathname || '/dashboard' }
            });
            setToast({ show: true, message: t('elementFound').replace('{name}', elData?.name || elHash), type: 'success' });
            return;
          }

          const content = await getContentByHash(trimmed);

          if (content?.type === 'element') {
            navigate(`/feed/${trimmed}`, {
              state: { elementData: content.data.data, chapters: content.data.chapters, related: content.data.related, sourcePage: location.pathname || '/dashboard' }
            });
            setToast({ show: true, message: t('elementFound').replace('{name}', content.data.data.name), type: 'success' });
          } else if (content?.type === 'collection') {
            navigate(`/collection/${trimmed}`, {
              state: { collectionData: content.data.data, chapters: content.data.chapters, related: content.data.related, sourcePage: location.pathname || '/dashboard' }
            });
            setToast({ show: true, message: t('collectionFound').replace('{name}', content.data.data.name), type: 'success' });
          } else {
            setUnknownQRModal({ isOpen: true, scannedCode: trimmed });
          }
        } catch (_lookupError) {
          setUnknownQRModal({ isOpen: true, scannedCode: trimmed });
        }
        return;
        setUnknownQRModal({ isOpen: true, scannedCode: trimmed });
      }
    } catch (error) {
      console.error('Error handling QR scan:', error);
      if (error.message && error.message.includes('Authentication failed')) {
        setToast({ show: true, message: t('sessionExpired') || 'Your session has expired. Please login again.', type: 'error' });
        return;
      }
      const msg = (error.message || '').toLowerCase();
      const isNotFound = msg.includes('not found') || msg.includes('404') || msg.includes('no item') || msg.includes('eannotfound');
      if (isNotFound) {
        const trimmed = (data || '').trim();
        setUnknownQRModal({ isOpen: true, scannedCode: trimmed });
        return;
      }
      setToast({ show: true, message: `QR Scan Error: ${error.message || error.toString() || 'Unknown error'}`, type: 'error' });
    }
  };

  return (
    <div className="bottom-navigation">
      <div className="nav-background">
        {/* Schmalerer Ausschnitt: von 135px auf ~103px Breite reduziert */}
        <svg className="nav-background-svg" width="100%" height="72" viewBox="0 0 440 72" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ filter: 'drop-shadow(0 -3px 10px rgba(0, 0, 0, 0.25))', WebkitFilter: 'drop-shadow(0 -3px 10px rgba(0, 0, 0, 0.25))' }}>
          <path d="M436 0C438.209 0 440 1.79086 440 4V68C440 70.2091 438.209 72 436 72H4C1.79086 72 0 70.2091 0 68V4C0 1.79086 1.79086 0 4 0H169C170.903 0 172.521 1.34767 173.051 3.17462C177.887 19.8277 192.256 32 209 32H231C248.744 32 263.113 19.8277 267.949 3.17462C268.479 1.34768 270.097 0 272 0H436Z" fill="white"/>
        </svg>
      </div>
      <div className="nav-content">
        <div className="nav-section left">
          <button 
            className={`nav-item ${activeNavItem === 'Home' ? 'active' : ''}`}
            onClick={() => navigate('/', { state: { clearFilters: true } })}
          >
            <div className={`nav-icon-container ${activeNavItem === 'home' ? 'active' : ''}`}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M3 9.5L12 2.5L21 9.5V20.5C21 21.0523 20.5523 21.5 20 21.5H15V14.5H9V21.5H4C3.44772 21.5 3 21.0523 3 20.5V9.5Z" 
                  stroke={activeNavItem === 'home' ? '#FF6407' : '#A9ADAD'} 
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                />
              </svg>
            </div>
            <span style={{ color: activeNavItem === 'home' ? '#FF6407' : '#A9ADAD' }}>
              {t('home') || 'Startseite'}
            </span>
          </button>
          <button 
            className={`nav-item ${activeNavItem === 'myCourses' ? 'active' : ''}`}
            onClick={() => navigate('/my-courses', { state: { clearFilters: true } })}
          >
            <div className={`nav-icon-container ${activeNavItem === 'myCourses' ? 'active' : ''}`}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M2 3H8C9.06087 3 10.0783 3.42143 10.8284 4.17157C11.5786 4.92172 12 5.93913 12 7V21C12 20.2044 11.6839 19.4413 11.1213 18.8787C10.5587 18.3161 9.79565 18 9 18H2V3Z" stroke={activeNavItem === 'myCourses' ? '#FF6407' : '#A9ADAD'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 3H16C14.9391 3 13.9217 3.42143 13.1716 4.17157C12.4214 4.92172 12 5.93913 12 7V21C12 20.2044 12.3161 19.4413 12.8787 18.8787C13.4413 18.3161 14.2044 18 15 18H22V3Z" stroke={activeNavItem === 'myCourses' ? '#FF6407' : '#A9ADAD'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span>{t('myCourses')}</span>
          </button>
        </div>

        {/* Floating Action Button — nur noch QR-Scanner, zentriert und vergrößert */}
        <div className="floating-buttons">
          <button 
            className={`floating-button camera-button${cameraLongPress ? ' camera-button--pressing' : ''}`}
            onClick={handleCameraClick}
            onPointerDown={handleCameraPointerDown}
            onPointerUp={handleCameraPointerUp}
            onPointerCancel={handleCameraPointerUp}
            onPointerLeave={handleCameraPointerUp}
            onContextMenu={e => e.preventDefault()}
          >
            {/* 70×70 (war 64×64) — viewBox unverändert, skaliert proportional */}
            <svg width="70" height="70" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g filter="url(#filter0_d_540_116)">
                <path d="M4 24C4 10.7452 14.7452 0 28 0C41.2548 0 52 10.7452 52 24C52 37.2548 41.2548 48 28 48C14.7452 48 4 37.2548 4 24Z" fill="#FF6407" shapeRendering="crispEdges"/>
                <path d="M21.1027 16.2334C20.8627 16.6132 20.5424 16.936 20.1644 17.179C19.7865 17.422 19.3599 17.5794 18.9147 17.64C18.408 17.712 17.9053 17.7894 17.4027 17.8734C15.9987 18.1067 15 19.3427 15 20.7654V32C15 32.7957 15.3161 33.5587 15.8787 34.1213C16.4413 34.684 17.2044 35 18 35H38C38.7957 35 39.5587 34.684 40.1213 34.1213C40.6839 33.5587 41 32.7957 41 32V20.7654C41 19.3427 40 18.1067 38.5973 17.8734C38.0943 17.7895 37.5902 17.7118 37.0853 17.64C36.6403 17.5792 36.214 17.4217 35.8363 17.1787C35.4586 16.9358 35.1385 16.6131 34.8987 16.2334L33.8027 14.4787C33.5565 14.0788 33.2176 13.7442 32.8147 13.503C32.4118 13.2619 31.9567 13.1214 31.488 13.0934C29.1643 12.9686 26.8357 12.9686 24.512 13.0934C24.0433 13.1214 23.5882 13.2619 23.1853 13.503C22.7824 13.7442 22.4435 14.0788 22.1973 14.4787L21.1027 16.2334Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M34 25C34 26.5913 33.3679 28.1174 32.2426 29.2426C31.1174 30.3679 29.5913 31 28 31C26.4087 31 24.8826 30.3679 23.7574 29.2426C22.6321 28.1174 22 26.5913 22 25C22 23.4087 22.6321 21.8826 23.7574 20.7574C24.8826 19.6321 26.4087 19 28 19C29.5913 19 31.1174 19.6321 32.2426 20.7574C33.3679 21.8826 34 23.4087 34 25ZM37 22H37.0107V22.0107H37V22Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
              <defs>
                <filter id="filter0_d_540_116" x="0" y="0" width="56" height="56" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                  <feOffset dy="4"/>
                  <feGaussianBlur stdDeviation="2"/>
                  <feComposite in2="hardAlpha" operator="out"/>
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.16 0"/>
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_540_116"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_540_116" result="shape"/>
                </filter>
              </defs>
            </svg>
          </button>

          {/* Mic-Button temporär deaktiviert — für spätere Reaktivierung:
          <button 
            className="floating-button mic-button"
            onClick={() => setIsVoiceRecorderOpen(true)}
          >
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
              ...mic SVG...
            </svg>
          </button>
          */}
        </div>

        <div className="nav-section right">
          <button 
            className={`nav-item ${activeNavItem === 'challenges' ? 'active' : ''}`}
            onClick={() => navigate('/challenges', { state: { clearFilters: true } })}
          >
            <div className={`nav-icon-container ${activeNavItem === 'challenges' ? 'active' : ''}`}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M2 2L12 7L22 2V17C22 17.5304 21.7893 18.0391 21.4142 18.4142C21.0391 18.7893 20.5304 19 20 19H4C3.46957 19 2.96086 18.7893 2.58579 18.4142C2.21071 18.0391 2 17.5304 2 17V2Z" stroke={activeNavItem === 'challenges' ? '#FF6407' : '#A9ADAD'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 7L12 12L22 7" stroke={activeNavItem === 'challenges' ? '#FF6407' : '#A9ADAD'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span>{t('challenges')}</span>
          </button>
          <button 
            className={`nav-item ${activeNavItem === 'profile' ? 'active' : ''}`}
            onClick={() => navigate('/profile')}
          >
            <div className={`nav-icon-container ${activeNavItem === 'profile' ? 'active' : ''}`} style={{ position: 'relative' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke={activeNavItem === 'profile' ? '#FF6407' : '#A9ADAD'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="7" r="4" stroke={activeNavItem === 'profile' ? '#FF6407' : '#A9ADAD'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute', top: '-4px', right: '-6px',
                  background: '#FF3B30', color: '#fff', fontSize: '10px', fontWeight: '700',
                  lineHeight: 1, minWidth: '16px', height: '16px', borderRadius: '99px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 4px', boxShadow: '0 0 0 2px #fff',
                  fontFamily: '-apple-system, sans-serif',
                }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <span>{t('profile')}</span>
          </button>
        </div>
      </div>
      
      {/* QR Scanner Modal */}
      <QRScanner
        isOpen={isQRScannerOpen}
        onClose={() => setIsQRScannerOpen(false)}
        onScan={handleQRScan}
      />
      
      {/* Voice Recorder — temporär deaktiviert
      <VoiceRecorder
        isOpen={isVoiceRecorderOpen}
        onClose={() => setIsVoiceRecorderOpen(false)}
        onSearchResults={handleVoiceSearchResults}
        onRagAnswer={onRagAnswer}
      />
      */}
      
      {/* Toast Notifications */}
      <Toast
        isVisible={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />

      {/* Unknown QR Code Modal */}
      {unknownQRModal.isOpen && createPortal(
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 2147483640,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
          }}
          onClick={() => setUnknownQRModal({ isOpen: false, scannedCode: null })}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#1f2937', borderRadius: '20px', padding: '32px 28px',
              maxWidth: '360px', width: '100%',
              boxShadow: '0 20px 50px rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.1)',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '52px', marginBottom: '16px' }}>🔍</div>
            <h2 style={{ color: '#fff', fontSize: '20px', fontWeight: '700', margin: '0 0 10px 0' }}>
              {t('unknownQRCodeTitle') || 'Unbekannter QR-Code'}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', margin: '0 0 12px 0', wordBreak: 'break-all', fontFamily: 'monospace' }}>
              {unknownQRModal.scannedCode}
            </p>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '15px', margin: '0 0 28px 0', lineHeight: '1.5' }}>
              {t('unknownQRCodeQuestion') || 'Wie soll ich weiter verfahren?'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={() => {
                  const code = unknownQRModal.scannedCode;
                  setUnknownQRModal({ isOpen: false, scannedCode: null });
                  navigate('/create-course', { state: { prefilledQRCode: code } });
                }}
                style={{
                  background: '#FF6407', color: '#fff', border: 'none',
                  borderRadius: '50px', padding: '14px 24px',
                  fontSize: '16px', fontWeight: '700', cursor: 'pointer'
                }}
              >
                {t('unknownQRCodeCreateCollection') || 'Neue Collection anlegen'}
              </button>
              <button
                onClick={() => setUnknownQRModal({ isOpen: false, scannedCode: null })}
                style={{
                  background: 'transparent', color: 'rgba(255,255,255,0.6)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '50px', padding: '13px 24px',
                  fontSize: '15px', fontWeight: '500', cursor: 'pointer'
                }}
              >
                {t('cancel') || 'Abbrechen'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default BottomNavigation;
